"use strict";

// UIButton
// UIStatesButton
// UIDragButton
// UIToggle
// UISwitch

import { Application } from "../../main/application.js";
import { Mouse } from "../../systems/core/mouse.js";
import { UIContainer } from "../uiContainer.js";
import { UISprite } from "../uiSprite.js";
import { UINineSliceSprite } from "../uiNineSliceSprite.js";

export class UIButton extends UIContainer {
    constructor(config) {
        let _onInitialize = config.onInitialize;
        config.onInitialize = undefined;

        super(config);

        this.id = config.id;

        UIButton.setupGraphics(this, config);
        if (this.graphics !== undefined) {
            this.graphics.setParent(this);

            if (config.copyGraphicsSize) {
                this.rect.size = {...this.graphics.calculatedRect.size};
                this.updateNow();
            }
        }

        this.interactive = true;
        this._useButtonMode = config.buttonMode ?? true;
        this.buttonMode = this._useButtonMode;
        this.enabled = true;
        this._state = 0;

        this.onPointerOver = config.onPointerOver;
        this.onPointerDown = config.onPointerDown;
        this.onPointerMove = config.onPointerMove;
        this.onPointerUp = config.onPointerUp;
        this.onPointerOut = config.onPointerOut;
        this.onPointerUpOutside = config.onPointerUpOutside;

        this._isDown = false;

        this.on(Application.main.input.over, () => this._onPointerOver());
        this.on(Application.main.input.down, (event) => this._onPointerDown(event.data.global));
        this.on(Application.main.input.move, (event) => this._onPointerMove(event.data.global));
        this.on(Application.main.input.up, () => this._onPointerUp());
        this.on(Application.main.input.out, () => this._onPointerOut());
        this.on(Application.main.input.upoutside, () => this._onPointerUpOutside());

        if (config.visible !== undefined && !config.visible) {
            this.hide();
        }

        if (config.enabled !== undefined && !config.enabled) {
            this.disable();
        }

        _onInitialize?.bind(this)(config);
        this.updateNow();
    }

    get state() {
        return this._state;
    }

    enable() {
        this.interactive = true;
        this.buttonMode = this._useButtonMode;
        this.enabled = true;
    }
    disable() {
        this.interactive = false;
        this.buttonMode = false;
        this.enabled = false;
    }

    show(enable) {
        this.visible = true;

        if (enable) {
            this.enable();
        } else if (enable !== undefined) {
            this.disable();
        }
    }
    hide() {
        if (this._state == 2 || this._state == 3) {
            this.emit(Application.main.input.up);
        }
        this.disable();
        this.visible = false;
    }

    _onPointerOver() {
        this._state = 1;
        this.onPointerOver?.(this.id);
    }

    _onPointerDown(pointer) {
        this._state = 2;
        if (this._isDown) return;
        this._isDown = true;
        this.onPointerDown?.(this.id, pointer);
    }

    _onPointerMove(pointer) {
        this._state = 3;
        if (!this._isDown) return;
        this.onPointerMove?.(this.id, pointer);
    }

    _onPointerUp() {
        this._state = 4;
        if (!this._isDown) return;
        this._isDown = false;
        this.onPointerUp?.(this.id);
    }

    _onPointerOut() {
        this._state = 0;
        this.onPointerOut?.(this.id);
    }

    _onPointerUpOutside() {
        this._state = 0;
        if (!this._isDown) return;
        this._isDown = false;
        this.onPointerUpOutside?.(this.id);
    }

    registerEvent(event,func) {
        if (func === undefined || typeof func != "function") return;

        switch (event) {
            case "over":
                this.on(Application.main.input.over, (event) => func(event.data.global, this.id));
                break;
            case "down":
                this.on(Application.main.input.down, (event) => func(event.data.global, this.id));
                break;
            case "move":
                this.on(Application.main.input.move, (event) => func(event.data.global, this.id));
                break;
            case "up":
                this.on(Application.main.input.up, (event) => func(event.data.global, this.id));
                break;
            case "upoutside":
                this.on(Application.main.input.upoutside, (event) => func(event.data.global, this.id));
                break;
            case "out":
                this.on(Application.main.input.out, (event) => func(event.data.global, this.id));
                break;
        }
    }

    //
    // Statics
    //

    static setupGraphics(button,config) {
        if (config.graphics !== undefined) {
            button.graphics = config.graphics;
            return;
        }

        if (config.cornerSize !== undefined) {
            button.graphics = new UINineSliceSprite({
                textureID: config.textureID,
                texture: config.texture,
                cornerSize: config.cornerSize,
                rect: { 
                    anchorX: { min: 0, max: 1 },
                    anchorY: { min: 0, max: 1 }
                }
            });
            return;
        }

        button.graphics = new UISprite({
            textureID: config.textureID,
            texture: config.texture,
            rect: { 
                anchorX: { min: 0, max: 1 },
                anchorY: { min: 0, max: 1 }
            }
        });
    }

    static getClearedConfigFromEvents(config) {
        config.onPointerDown = undefined;
        config.onPointerDownState = undefined;
        config.onPointerOut = undefined;
        config.onPointerOutState = undefined;
        config.onPointerOver = undefined;
        config.onPointerOverState = undefined;
        config.onPointerMove = undefined;
        config.onPointerMoveState = undefined;
        config.onPointerUp = undefined;
        config.onPointerUpState = undefined;
        config.onPointerUpOutside = undefined;
        config.onPointerUpOutsideState = undefined;
        config.onPointerInactive = undefined;
        config.onPointerInactiveState = undefined;
        config.onPointerActive = undefined;
        config.onPointerActiveState = undefined;
        return config;
    }
}

export class UIStatesButton extends UIButton {
    constructor(config) {
        let _onInitialize = config.onInitialize;
        config.onInitialize = undefined;

        super(UIButton.getClearedConfigFromEvents({...config}));

        this.onPointerOverState = config.onPointerOverState;
        this.onPointerDownState = config.onPointerDownState;
        if (Application.main.device.isDesktop) {
            this.onPointerUpState = config.onPointerUpState;
        } else {
            this.onPointerUpState = config.onPointerOutState;
        }
        this.onPointerOutState = config.onPointerOutState;
        this.onPointerUpOutsideState = config.onPointerUpOutsideState;

        if (config.onPointerInactiveState !== undefined) {
            this.onPointerInactiveState = config.onPointerInactiveState;
            this.onPointerInactive = config.onPointerInactive;
        }
        if (config.onPointerActive !== undefined) {
            this.onPointerActive = config.onPointerActive;
        }

        this.onPointerOver = config.onPointerOver;
        this.onPointerDown = config.onPointerDown;
        this.onPointerMove = config.onPointerMove;
        this.onPointerUp = config.onPointerUp;
        this.onPointerOut = config.onPointerOut;
        this.onPointerUpOutside = config.onPointerUpOutside;

        if (config.enabled !== undefined && !config.enabled) {
            this.disable();
        }

        _onInitialize?.bind(this)(config);
        this.updateNow();
    }

    enable() {
        super.enable();

        if (this.onPointerOutState !== undefined) {
            this.graphics?.setTexture(this.onPointerOutState);
        }
        
        this.onPointerActive?.(this.id);
    }
    disable() {
        super.disable();

        if (this.onPointerInactiveState !== undefined) {
            this.graphics?.setTexture(this.onPointerInactiveState);

            this.onPointerInactive?.(this.id);
        }
    }

    _onPointerOver() {
        this._state = 1;

        if (this.onPointerOverState !== undefined) {
            this.graphics?.setTexture(this.onPointerOverState);
        }

        this.onPointerOver?.(this.id);
    }

    _onPointerDown(pointer) {
        this._state = 2;

        if (this.onPointerDownState !== undefined) {
            this.graphics?.setTexture(this.onPointerDownState);
        }

        this.onPointerDown?.(this.id, pointer);
    }

    _onPointerMove(pointer) {
        this._state = 3;

        this.onPointerMove?.(this.id, pointer);
    }

    _onPointerUp() {
        this._state = 4;

        if (this.onPointerUpState !== undefined) {
            this.graphics?.setTexture(this.onPointerUpState);
        }

        this.onPointerUp?.(this.id);
    }

    _onPointerOut() {
        this._state = 0;

        if (this.onPointerOutState !== undefined) {
            this.graphics?.setTexture(this.onPointerOutState);
        }

        this.onPointerOut?.(this.id);
    }

    _onPointerUpOutside() {
        this._state = 0;

        if (this.onPointerUpOutsideState !== undefined) {
            this.graphics?.setTexture(this.onPointerUpOutsideState);
        }

        this.onPointerUpOutside?.(this.id);
    }
}

export class UIDragButton extends UIButton {
    constructor(config) {
        config.buttonMode = config.buttonMode ?? false;

        let _onInitialize = config.onInitialize;
        config.onInitialize = undefined;

        super(config);

        this._onDrag = config.onDrag;
        this._onDragEnded = config.onDragEnded;

        this._isDown = false;
        this._startX = 0;
        this._startY = 0;
        this._prevX = 0;
        this._prevY = 0;
        this.isBeingDragged = false;

        _onInitialize?.bind(this)(config);
        this.updateNow();
    }

    get OffsetX() { return this._startX; }
    get OffsetY() { return this._startY; }
    get PrevX() { return this._prevX; }
    get PrevY() { return this._prevY; }

    _onPointerDown(pointer) {
        this._state = 1;
        
        this._isDown = true;
        this._prevX = pointer.x;
        this._prevY = pointer.y;
        this._startX = this._prevX - this.getRealWorldPosition().x;
        this._startY = this._prevY - this.getRealWorldPosition().y;
        this.onPointerDown?.(this.id, pointer);
    }

    _onPointerMove(pointer,doDragging) {
        this._state = 3;

        if (this._isDown && !this.isBeingDragged) {
            this.isBeingDragged = true;
            // Located in Main.js
            Mouse.disableMouseMoveFlag();
        }

        if (this.isBeingDragged) {
            if (doDragging === undefined || doDragging) {
                this._onDrag?.(this.id, pointer);
            }
            this._prevX = pointer.x;
            this._prevY = pointer.y;
        }

        this.onPointerMove?.(this.id);
    }

    _onPointerUp() {
        this._state = 4;

        this._isDown = false;
        this.isBeingDragged = false;
        // Located in Main.js
        Mouse.enableMouseMoveFlag()
        this._onDragEnded?.();
        this.onPointerUp?.(this.id);
    }

    _onPointerUpOutside() {
        this._state = 0;

        this._onPointerUp();
        this.onPointerUpOutside?.(this.id);
    }
}

export class UIToggle extends UIButton {
    constructor(config) {
        let _onInitialize = config.onInitialize;
        config.onInitialize = undefined;

        super(config);

        this.on(Application.main.input.down, () => this.toggle());
        this.tick = config.tick;
        this.tick.setParent(this);

        this.toggled = config.toggled ?? false;
        this._onToggled = config.onToggled;
        this._onChangedState = config.onChangedState;

        this._updateToggleState();

        _onInitialize?.bind(this)(config);
        this.updateNow();
    }

    toggleOn(fireCallback) {
        this.toggled = true;
        this._updateToggleState();
        if (fireCallback === undefined || fireCallback) {
            this._onToggled?.(this.toggled);
        }
    }
    toggleOff(fireCallback) {
        this.toggled = false;
        this._updateToggleState();
        if (fireCallback === undefined || fireCallback) {
            this._onToggled?.(this.toggled);
        }
    }
    toggle(fireCallback) {
        this.toggled = !this.toggled;
        this._updateToggleState();
        if (fireCallback === undefined || fireCallback) {
            this._onToggled?.(this.toggled);
        }
    }

    _updateToggleState() {
        this._onChangedState?.(this.toggled);
    }
}

export class UISwitch extends UIButton {
    constructor(config) {
        let _onInitialize = config.onInitialize;
        config.onInitialize = undefined;
        
        super(config);

        this.on(Application.main.input.down, () => this.toggle());
        this.pointer = config.pointer;
        this.pointer.setParent(this);

        this.toggled = config.toggled === undefined ? false : config.toggled;
        this._onToggled = config.onToggled;
        this._onChangedState = config.onChangedState;

        this._pointerOnPosition = 0;
        this._pointerOffPosition = 0;
        if (config.pointerStatesOffset !== undefined) {
            this._pointerOnPosition = config.pointerStatesOffset;
            this._pointerOffPosition = -config.pointerStatesOffset;
        }
        
        this._updateToggleState();

        _onInitialize?.bind(this)(config);
        this.updateNow();
    }

    toggleOn() {
        this.toggled = true;
        this._updateToggleState();
        this._onToggled?.(this.toggled);
    }
    toggleOff() {
        this.toggled = false;
        this._updateToggleState();
        this._onToggled?.(this.toggled);
    }
    toggle(value) {
        this.toggled = value ?? !this.toggled;
        this._updateToggleState();
        this._onToggled?.(this.toggled);
    }

    _updateToggleState() {
        this.pointer.rect.position.x = this.toggled ? this._pointerOnPosition : this._pointerOffPosition;
        this._onChangedState?.(this.toggled);
        this.updateNow();
    }
}